import { useMemo, useState } from "react";
import { useDashboardData } from "@/shared/contexts/DashboardDataContext";
import styles from "./CalendarView.module.css";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import navArrowLeftIcon from "@/assets/icons/navArrowLeft.svg";
import navArrowLeftWhiteIcon from "@/assets/icons/navArrowLeftWhite.svg";
import navArrowRightIcon from "@/assets/icons/navArrowRight.svg";
import navArrowRightWhiteIcon from "@/assets/icons/navArrowRightWhite.svg";

type Props = {
  date: Date;
  onSelectDate: (d: Date) => void;
  onMonthChange?: (d: Date) => void;
};

export default function CalendarView({ date, onSelectDate, onMonthChange }: Props) {
  const { darkMode } = useDarkMode();
  const { entries, exportedEntries } = useDashboardData();
  const [currentMonthDate, setCurrentMonthDate] = useState<Date>(new Date(date));

  const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const sums = useMemo(() => {
    const s: Record<string, { tracked: number; exp: number }> = {};
    for (const e of entries) {
      const d = new Date(e.date);
      const k = dayKey(d);
      s[k] = s[k] || { tracked: 0, exp: 0 };
      s[k].tracked += e.duration;
    }
    for (const e of exportedEntries) {
      const d = new Date(e.date);
      const k = dayKey(d);
      s[k] = s[k] || { tracked: 0, exp: 0 };
      s[k].exp += e.duration;
    }
    return s;
  }, [entries, exportedEntries]);

  // Build 6x7 grid days for currentMonthDate
  const calendarDays = useMemo(() => {
    const firstOfMonth = new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth(), 1);
    const startDay = new Date(firstOfMonth);
    const startWeekday = startDay.getDay(); // 0 Sun .. 6 Sat
    // start from Sunday before/at first
    startDay.setDate(startDay.getDate() - startWeekday);
    const days: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(startDay);
      d.setDate(startDay.getDate() + i);
      days.push(d);
    }
    return days;
  }, [currentMonthDate]);

  const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const goToPreviousMonth = () => {
    const d = new Date(currentMonthDate);
    d.setMonth(d.getMonth() - 1);
    setCurrentMonthDate(d);
    onMonthChange?.(d);
  };
  const goToNextMonth = () => {
    const d = new Date(currentMonthDate);
    d.setMonth(d.getMonth() + 1);
    setCurrentMonthDate(d);
    onMonthChange?.(d);
  };

  return (
    <div className={styles.calendarView}>
        <div className={styles.calendarHeader}>
          <span className={styles.calendarHeaderLabel}>
            {currentMonthDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
          </span>
          <div className={styles.calendarHeaderButtonWrapper}>
            <button className={styles.calendarPageButton} onClick={goToPreviousMonth}>
              <img src={darkMode ? navArrowLeftWhiteIcon : navArrowLeftIcon} alt="Prev" />
            </button>
            <button
              className={`${styles.calendarPageButton} ${styles.calendarTodayButton}`}
              onClick={() => {
                const now = new Date();
                setCurrentMonthDate(new Date(now));
                onSelectDate(now);
              }}
            >
              Today
            </button>
            <button className={styles.calendarPageButton} onClick={goToNextMonth}>
              <img src={darkMode ? navArrowRightWhiteIcon : navArrowRightIcon} alt="Next" />
            </button>
          </div>
        </div>

        <div className={styles.calendarGrid}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className={styles.calendarWeekdayHeader}>{day}</div>
          ))}
          {calendarDays.map((currDay, index) => {
            const isCurrentMonth = currDay.getMonth() === currentMonthDate.getMonth();
            const isWeekend = currDay.getDay() === 0 || currDay.getDay() === 6;
            const key = dayKey(currDay);
            const tracked = (sums[key]?.tracked || 0).toFixed(1);
            const exp = (sums[key]?.exp || 0).toFixed(1);

            return (
              <button
                key={index}
                onClick={() => onSelectDate(currDay)}
                data-selected={isSameDay(currDay, date)}
              >
                <div
                  className={`${styles.calendarCard} ${
                    !isCurrentMonth ? styles.calendarOutsideMonthCard : ""
                  } ${isWeekend ? styles.calendarWeekendCard : ""}`}
                  data-today={isSameDay(currDay, new Date())}
                  data-hours={`${tracked}h`}
                >
                  <div className={styles.cardDate}>{currDay.getDate()}</div>
                  <div
                    className={`${styles.cardMetric} ${styles.cardMetricUnits}`}
                  >
                    {tracked} hrs to export
                  </div>
                  <div
                    className={`${styles.cardMetric} ${styles.cardMetricUnits}`}
                  >
                    {exp} hrs exported
                  </div>
                </div>
              </button>
            );
          })}
        </div>
    </div>
  );
}
